import {
    ComponentKind,
    getPrimitiveColumns,
    PropertyDescription,
    InputOutputTables,
    TableGlideType,
    TableColumn,
    isPrimitiveType,
    MutatingScreenKind,
    DefaultableComponentDescription,
    makeEmptyComponentDescription,
} from "../description";
import { ColumnPropertyHandler, ColumnPropertyFlag, isRequiredPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import { ComponentHandlerBase } from "./handler";
import { ComponentBuilder, ComponentConfiguratorContext, ComponentDescriptor, PropertyDescriptor } from ".";
import { SerializedComponent } from "../../components/SerializedApp";
import { assert } from "../../Support";
import { makeColumnProperty } from "./property-utils";
import { getDocURL } from "../../docUrl";
import { setColumnInRowForEditComponent, getDefaultValuesForComponent, makeText } from "../builder-utils";
import { makeCaptionStringPropertyDescriptor, makeDefaultValuePropertyDescriptor } from "./descriptor-utils";

const ComponentKindFilePicker: ComponentKind = "file-picker";

const propertyNamePropertyHandler = new ColumnPropertyHandler(
    "propertyName",
    "Column",
    [
        ColumnPropertyFlag.Required,
        ColumnPropertyFlag.Editable,
        ColumnPropertyFlag.EditedInApp,
        ColumnPropertyFlag.DefaultCaption,
    ],
    undefined,
    ["file", "upload"],
    getPrimitiveColumns,
    "uri",
    PropertySection.Data
);

interface FilePickerComponentDescription extends DefaultableComponentDescription {
    readonly caption: PropertyDescription | undefined;
}

// FIXME: Generalize this with `ImagePickerComponentHandler`
export class FilePickerComponentHandler extends ComponentHandlerBase<FilePickerComponentDescription> {
    constructor() {
        super(ComponentKindFilePicker);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public needValidation(): boolean {
        return true;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: FilePickerComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const propertyName = propertyNamePropertyHandler.getColumnName(desc);
        if (propertyName === undefined) return b.emptyUI;

        const setColumnsResult = setColumnInRowForEditComponent(
            b,
            mutatingScreenKind,
            propertyName,
            b.getProperty(propertyName, outputContext, tables.output),
            undefined,
            tables.output,
            undefined,
            b.getContext,
            { debounce: false }
        );
        if (setColumnsResult === undefined) return b.emptyUI;
        const { lets, valueToModify, action, isEnabled } = setColumnsResult;

        const isRequired = isRequiredPropertyHandler.getSwitch(desc);
        return b.let(
            lets,
            b.ui("FilePicker", {
                value: valueToModify,
                onChange: action,
                isEnabled,
                validFlag: isRequired || mutatingScreenKind !== undefined ? validFlag : undefined,
                caption: makeText(builder, desc.caption, tables.input, true),
                isRequired: b.constant(isRequired),
            })
        );
    }

    public getDescriptor(
        _desc: FilePickerComponentDescription,
        _table: TableGlideType | undefined,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<FilePickerComponentDescription> {
        const properties: PropertyDescriptor[] = [
            propertyNamePropertyHandler,
            makeCaptionStringPropertyDescriptor("File", false, mutatingScreenKind),
        ];
        if (mutatingScreenKind !== undefined) {
            properties.push(isRequiredPropertyHandler);

            const defaultDescr = makeDefaultValuePropertyDescriptor(ccc, mutatingScreenKind, "uri");
            if (defaultDescr !== undefined) {
                properties.push(defaultDescr);
            }
        }
        properties.push(...this.getBasePropertyDescriptors());
        return {
            name: "File Picker",
            description: "Upload any file",
            img: "componentFilePicker",
            group: "Pickers",
            helpUrl: getDocURL("filePicker"),
            properties,
        };
    }

    public getDefaultValues(
        builder: ComponentBuilder,
        desc: FilePickerComponentDescription,
        { input }: InputOutputTables
    ): readonly [string, SerializedComponent][] {
        return getDefaultValuesForComponent(builder, desc, input, "uri");
    }

    public static defaultComponent(column: TableColumn): FilePickerComponentDescription {
        assert(isPrimitiveType(column.type));
        return {
            ...makeEmptyComponentDescription(ComponentKindFilePicker),
            propertyName: makeColumnProperty(column.name),
            defaultValue: undefined,
            caption: undefined,
        };
    }
}
